import { getDocument, updateDocument, subscribeToDocument } from '../firestore.service';
import type { UserSelectionMode, RerollMode } from '../../features/canvas/utils/randomization';

export interface UserPreferences {
  preferences?: {
    sidebarCollapsed?: boolean;
    theme?: string;
    selectionMode?: UserSelectionMode;
    // Préférence de reroll GLOBALE (feature « Reroll settings », section
    // « Reroll » de /settings/reroll) : random / increment / decrement.
    rerollMode?: RerollMode;
    // Switch « Reroll » de la même section : false = reroll désactivé (mode
    // effectif 'loop' — aucun tirage ne change la sélection). Absent = true.
    rerollEnabled?: boolean;
  };
}

export const userPreferenceService = {
  async getPreferences(uid: string): Promise<UserPreferences | null> {
    return getDocument<UserPreferences>('users', uid);
  },

  async setSidebarCollapsed(uid: string, collapsed: boolean): Promise<void> {
    await updateDocument('users', uid, { 'preferences.sidebarCollapsed': collapsed });
  },

  /**
   * Persiste le mode de sélection des prompts (issue #53) :
   * random / loop. Les modes increment / decrement vivent dans la préférence
   * séparée `rerollMode` (feature « Reroll settings », setRerollMode).
   */
  async setSelectionMode(uid: string, mode: UserSelectionMode): Promise<void> {
    await updateDocument('users', uid, { 'preferences.selectionMode': mode });
  },

  /**
   * Souscription temps réel au mode de sélection (sync multi-appareils).
   * Retourne null tant qu'aucune préférence n'existe (défaut = random côté store).
   */
  subscribeSelectionMode(
    uid: string,
    onChange: (mode: UserSelectionMode | null) => void
  ): () => void {
    return subscribeToDocument<UserPreferences>('users', uid, (data) => {
      onChange(data?.preferences?.selectionMode ?? null);
    });
  },

  /**
   * Persiste la préférence de reroll GLOBALE (feature « Reroll settings ») :
   * random / increment / decrement.
   */
  async setRerollMode(uid: string, mode: RerollMode): Promise<void> {
    await updateDocument('users', uid, { 'preferences.rerollMode': mode });
  },

  /**
   * Souscription temps réel à la préférence de reroll (sync multi-appareils).
   * Retourne null tant qu'aucune préférence n'existe (défaut = random).
   */
  subscribeRerollMode(
    uid: string,
    onChange: (mode: RerollMode | null) => void
  ): () => void {
    return subscribeToDocument<UserPreferences>('users', uid, (data) => {
      onChange(data?.preferences?.rerollMode ?? null);
    });
  },

  /**
   * Persiste le switch « Reroll » de la section « Reroll » des settings :
   * false = reroll désactivé (mode effectif 'loop').
   */
  async setRerollEnabled(uid: string, enabled: boolean): Promise<void> {
    await updateDocument('users', uid, { 'preferences.rerollEnabled': enabled });
  },

  /**
   * Souscription temps réel au switch « Reroll » (sync multi-appareils).
   * Retourne null tant qu'aucune préférence n'existe (défaut = activé).
   */
  subscribeRerollEnabled(
    uid: string,
    onChange: (enabled: boolean | null) => void
  ): () => void {
    return subscribeToDocument<UserPreferences>('users', uid, (data) => {
      onChange(data?.preferences?.rerollEnabled ?? null);
    });
  },
};
